import React from "react";
import PropTypes from "prop-types";
import styles from "./EagleItem.module.css";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

const trendColors = {
  up: "#43BA32",
  same: "#FF9A00",
  down: "#ED1F24",
};

export function EagleItem({
  name,
  avatar,
  onClick,
  totalBonus,
  totalPremier,
  totalMajorFunctions,
  bonusLevelPercentage,
  pmLegs,
  majorFunctions,
  pvOnly,
  sponsorName,
}) {
  const FlowerPattern = ({ images }) => {
    const isSingle = images.length === 1;

    return (
      <div
        className={classNames(styles.flower, {
          [styles.singleImage]: isSingle,
        })}
      >
        {isSingle ? (
          <div
            className={classNames(styles.circle, styles.center)}
            style={{ backgroundImage: `url(${images[0]})` }}
          ></div>
        ) : (
          <>
            <div
              className={classNames(styles.circle, styles.center)}
              style={{ backgroundImage: `url(${images[0]})` }}
            ></div>
            {images.slice(1).map((image, index) => {
              const angle = (360 / (images.length - 1)) * index;
              const transformStyle = {
                transform: `rotate(${angle}deg) translate(-10px, -10px) rotate(-${angle}deg)`,
                backgroundImage: `url(${image})`,
              };
              return (
                <div
                  key={index}
                  className={classNames(styles.circle, styles.petal)}
                  style={transformStyle}
                ></div>
              );
            })}
          </>
        )}
      </div>
    );
  };

  const getColor = (data) => trendColors[data?.trend] || "#000";
  const pad = (v) => (v != null ? v.toString().padStart(2, "0") : "00");
  const { t } = useTranslation();

  return (
    <div
      className={classNames(styles.container, {
        [styles.expandedContainer]: majorFunctions?.length > 0,
        [styles.clickable]: !bonusLevelPercentage && !pmLegs && !majorFunctions,
      })}
      onClick={onClick}
    >
      <div className={styles.leftColumn}>
        <div className={styles.leftContent}>
          <FlowerPattern images={avatar} />
        </div>
      </div>

      <div className={styles.centerColumn}>
        <span className={styles.firstName}>{name}</span>
        {sponsorName && (
          <span className={styles.sponsorName}>{sponsorName}</span>
        )}
      </div>

      <div className={styles.rightColumn}>
        {pvOnly && (
          <div
            className={`${styles.rightContent} ${styles.pvOnlyGroup}`}
            style={{ fontWeight: "bold" }}
          >
            <span className={styles.pvOnlyValue} style={{ color: "#43BA32" }}>
              {totalBonus?.value ?? "—"}
            </span>
          </div>
        )}
        {!pvOnly && !bonusLevelPercentage && !pmLegs && !majorFunctions && (
          <div
            className={`${styles.rightContent} ${styles.totalBonusGroup}`}
            style={{ fontWeight: "bold" }}
          >
            <span style={{ color: getColor(totalBonus) }}>
              {pad(totalBonus.value)}-
            </span>

            <span style={{ color: getColor(totalPremier) }}>
              {pad(totalPremier.value)}
            </span>

            <span style={{ color: getColor(totalMajorFunctions) }}>
              -{pad(totalMajorFunctions.value)}
            </span>
          </div>
        )}

        {bonusLevelPercentage?.value != null && (
          <div className={`${styles.rightContent} ${styles.bonusLevelGroup}`}>
            <span
              className={styles.label}
              style={{ color: getColor(bonusLevelPercentage) }}
            >
              {t("Bonus Level")}
            </span>
            <span
              className={styles.value}
              style={{ color: getColor(bonusLevelPercentage) }}
            >
              {bonusLevelPercentage.value}
            </span>
          </div>
        )}

        {pmLegs?.value != null && (
          <div className={`${styles.rightContent} ${styles.pmLegsGroup}`}>
            <span className={styles.label} style={{ color: getColor(pmLegs) }}>
              {t("PM Legs")}
            </span>
            <span className={styles.value} style={{ color: getColor(pmLegs) }}>
              {pmLegs.value}
            </span>
          </div>
        )}

        {majorFunctions && (
          <div className={styles.majorFunctionsWrapper}>
            <div className={styles.functionItem} style={{ color: "#3f7a81" }}>
              {majorFunctions}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

EagleItem.propTypes = {
  name: PropTypes.string.isRequired,
  avatar: PropTypes.arrayOf(PropTypes.string),
  onClick: PropTypes.func.isRequired,
  totalBonus: PropTypes.object,
  totalPremier: PropTypes.object,
  totalMajorFunctions: PropTypes.object,
  bonusLevelPercentage: PropTypes.object,
  pmLegs: PropTypes.object,
  majorFunctions: PropTypes.array,
};
